/**
 * pipelineView.ts — pure view logic for the Pipeline board (redesign slice 4, the
 * calm-minimal Pipeline board `docs/design/calm-minimal/Pipeline.dc.html`;
 * ADOPTION.md §16, register row "Redesign slice 4"). Component-free so vitest
 * pins every rule (the homeView pattern). Every figure derives from a read path
 * the page already has: the deals list (capped at 200 — the board displays
 * exactly those rows, so every lane figure describes what is on screen), the
 * server's open figures on /v1/sales/reports and its per-stage, all-time
 * totals on /v1/sales/stats. Nothing is typed in or fabricated.
 */
import type { SalesDeal, DealStage } from '../../types'
import type { SalesReports, SalesStats } from '../../../../api/types'
import { cardBadge, visibleSignals } from './dealHealth'
import type { DealHealthSignal } from '../../../../api/types'
import { excludedSuffix } from './fenceCopy'

export const LANES: DealStage[] = ['New Lead', 'Qualified', 'Proposal Sent', 'Negotiation', 'Closed Won', 'Closed Lost']
export const OPEN_LANES: DealStage[] = ['New Lead', 'Qualified', 'Proposal Sent', 'Negotiation']

/** The boards' sentence-case lane header ("New lead", "Proposal sent") — DISPLAY
 *  only: the DealStage value, every aria-label and every drop target keep the
 *  type's label, so nothing the inventory keys by moves. */
export function laneLabel(stage: DealStage): string {
  const s = String(stage)
  return s.charAt(0) + s.slice(1).toLowerCase()
}

/** "13d in stage" — the boards' card line over the 0097 stage stamp the list
 *  already carries (api.ts daysInStage). */
export function daysLabel(days: number): string {
  return `${days}d in stage`
}

/** The card's "Nd in stage" line IS the server's `stage_stale` signal rendered
 *  in place (the same stamp, the same day count), so the footer badge shows the
 *  worst of the OTHER signals and counts the rest — never the same sentence
 *  twice on one card. Board and Detail still agree on every signal: Detail
 *  lists them all, the board renders stage_stale as the age line's tone. */
export function footerBadge(health: readonly DealHealthSignal[] | null | undefined): { signal: DealHealthSignal; more: number } | null {
  return cardBadge(visibleSignals(health).filter(s => s.code !== 'stage_stale'))
}
/** True when the server flags the deal stale in its stage — the age line takes the warning tone. */
export function ageStale(deal: SalesDeal): boolean {
  return !!deal.health && deal.health.some(s => s.code === 'stage_stale')
}

/** An unassigned deal: mapDeal renders a missing owner as '—'. The card draws
 *  the boards' dashed ring for it — never a fabricated initial. */
export function unassigned(deal: SalesDeal): boolean {
  const o = (deal.owner ?? '').trim()
  return o === '' || o === '—'
}

/** The Owner chip's options: every assigned owner on the board, once, sorted. */
export function ownerOptions(deals: readonly SalesDeal[]): string[] {
  return [...new Set(deals.filter(d => !unassigned(d)).map(d => d.owner.trim()))].sort((a, b) => a.localeCompare(b))
}

export type CloseState = 'ok' | 'overdue' | 'missing'
/** The footer's close-date state: MISSING when the wire carries no expected
 *  close; OVERDUE when the SERVER's health verdict says so (code
 *  `close_overdue` — the board never re-derives a rule the server owns; a
 *  closed deal carries no verdict and is never overdue); otherwise OK. */
export function closeState(deal: SalesDeal): CloseState {
  if (!deal.expectedClose) return 'missing'
  if (deal.health && deal.health.some(h => h.code === 'close_overdue')) return 'overdue'
  return 'ok'
}

export type CloseFilter = 'any' | 'overdue' | 'this-month' | 'next-30' | 'none'
export const CLOSE_FILTERS: { value: CloseFilter; label: string }[] = [
  { value: 'any', label: 'Any time' }, { value: 'overdue', label: 'Overdue' }, { value: 'this-month', label: 'This month' },
  { value: 'next-30', label: 'Next 30 days' }, { value: 'none', label: 'No close date' },
]
export function matchesClose(deal: SalesDeal, f: CloseFilter, nowMs: number): boolean {
  if (f === 'any') return true
  const state = closeState(deal)
  if (f === 'none') return state === 'missing'
  if (f === 'overdue') return state === 'overdue'
  if (!deal.expectedClose) return false
  const t = Date.parse(deal.expectedClose)
  if (Number.isNaN(t)) return false
  if (f === 'next-30') return t >= nowMs && t <= nowMs + 30 * 86_400_000
  const now = new Date(nowMs), d = new Date(t)
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
}

export type SortKey = 'value' | 'close' | 'age' | 'name'
export const SORT_KEYS: { value: SortKey; label: string }[] = [
  { value: 'value', label: 'Value' }, { value: 'close', label: 'Close date' }, { value: 'age', label: 'Days in stage' }, { value: 'name', label: 'Name' },
]
/** Within a lane. Value sorts on the GBP-comparable leg, uncaptured rows last;
 *  close date ascending with missing dates last; age = days in stage, longest
 *  first; name by locale. Stable: ties keep the list's order. */
export function sortDeals(deals: readonly SalesDeal[], key: SortKey): SalesDeal[] {
  const rows = deals.map((d, i) => ({ d, i }))
  const num = (d: SalesDeal) => d.basePence ?? -1
  const close = (d: SalesDeal) => { const t = d.expectedClose ? Date.parse(d.expectedClose) : NaN; return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t }
  const cmp = (a: SalesDeal, b: SalesDeal): number => {
    if (key === 'value') return num(b) - num(a)
    if (key === 'close') return close(a) - close(b)
    if (key === 'age') return b.daysInStage - a.daysInStage
    return a.name.localeCompare(b.name)
  }
  rows.sort((a, b) => { const c = cmp(a.d, b.d); return c !== 0 ? c : a.i - b.i })
  return rows.map(r => r.d)
}

export interface BoardFilters { search: string; owner: string; close: CloseFilter; attentionOnly: boolean; nowMs: number }
/** The chips, applied to the rows the board displays. `owner` '' = anyone,
 *  '—' = unassigned, else an owner's name. Attention = an open deal the
 *  SERVER flagged (cardBadge, the same rule the card's badge renders). */
export function applyBoardFilters(deals: readonly SalesDeal[], f: BoardFilters): SalesDeal[] {
  const q = f.search.trim().toLowerCase()
  return deals.filter(d => {
    if (q && !(d.name.toLowerCase().includes(q) || d.company.toLowerCase().includes(q))) return false
    if (f.owner === '—' ? !unassigned(d) : f.owner !== '' && d.owner.trim() !== f.owner) return false
    if (!matchesClose(d, f.close, f.nowMs)) return false
    if (f.attentionOnly && !needsAttention(d)) return false
    return true
  })
}

export function needsAttention(deal: SalesDeal): boolean {
  return OPEN_LANES.includes(deal.stage) && cardBadge(deal.health) !== null
}
/** "3 deals need attention" — the open deals the server's health rules flagged. */
export function attentionCount(deals: readonly SalesDeal[]): number {
  return deals.filter(needsAttention).length
}
export function attentionLabel(n: number): string {
  return `${n} ${n === 1 ? 'deal needs' : 'deals need'} attention`
}

export interface LaneFigure { count: number; valuePence: number; sharePct: number; barPct: number }
/** Lane figures describe what the board DISPLAYS: the value per lane over the
 *  GBP-comparable leg (uncaptured rows drop out, the server's fence), an open
 *  lane's share of the OPEN total ("79% of open") and its bar against the
 *  largest open lane; a closed lane shows its all-time count and value with a
 *  full bar (its share of "open" is not a figure). */
export function laneFigures(deals: readonly SalesDeal[], lanes: readonly DealStage[] = LANES): Map<DealStage, LaneFigure> {
  const value = new Map<DealStage, number>(); const count = new Map<DealStage, number>()
  for (const s of lanes) { value.set(s, 0); count.set(s, 0) }
  for (const d of deals) {
    if (!value.has(d.stage)) continue
    value.set(d.stage, (value.get(d.stage) ?? 0) + (d.basePence ?? 0)); count.set(d.stage, (count.get(d.stage) ?? 0) + 1)
  }
  const openTotal = OPEN_LANES.reduce((s, l) => s + (value.get(l) ?? 0), 0)
  const largestOpen = Math.max(0, ...OPEN_LANES.map(l => value.get(l) ?? 0))
  const out = new Map<DealStage, LaneFigure>()
  for (const s of lanes) {
    const v = value.get(s) ?? 0
    const open = OPEN_LANES.includes(s)
    out.set(s, { count: count.get(s) ?? 0, valuePence: v, sharePct: open && openTotal > 0 ? Math.round((v / openTotal) * 100) : 0, barPct: open ? (largestOpen > 0 ? (v / largestOpen) * 100 : 0) : 100 })
  }
  return out
}
export function shareLabel(stage: DealStage, sharePct: number): string {
  return OPEN_LANES.includes(stage) ? `${sharePct}% of open` : 'All-time'
}

export interface HeadFigures { openPence: number; openCount: number; avgPence: number | null; won: { pence: number; count: number; excluded: string } | null }
/** The title row's four figures: the three open ones are the server's KPI
 *  formulas over the FULL deal set (/reports, never the capped list); the
 *  all-time won pair is the server's closed_won stage total on /stats — its
 *  money is POUNDS (a float), converted to pence here and nowhere else; its
 *  F-1d fence rides as "(+k excluded)". */
export function headFigures(reports: SalesReports | null | undefined, stats: SalesStats | null | undefined): HeadFigures {
  const wonStat = stats?.pipeline?.stages?.find(s => s.stage === 'closed_won')
  return {
    openPence: reports?.totalPipelinePence ?? 0,
    openCount: reports?.openDealCount ?? 0,
    avgPence: reports && reports.openDealCount > 0 ? reports.avgDealSizePence : null,
    won: wonStat ? { pence: Math.round(wonStat.totalValue * 100), count: wonStat.count, excluded: excludedSuffix(wonStat.excludedNonGbp) } : null,
  }
}
